import { Component, OnInit } from '@angular/core';
import { CommonModule, DatePipe, DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { TableModule } from 'primeng/table';
import { League, LeagueListResponse } from '../../models/league';
import { LeagueService } from '../../services/league.service';
import { AdminPageHeaderComponent } from '../shared/admin-page-header/admin-page-header.component';

@Component({
  selector: 'app-leagues',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    TableModule,
    DatePipe,
    DecimalPipe,
    AdminPageHeaderComponent,
  ],
  templateUrl: './leagues.component.html',
  styleUrls: ['./leagues.component.scss'],
})
export class LeaguesComponent implements OnInit {
  readonly title = 'Ligas';
  readonly subtitle = 'Crie e gerencie ligas com operação administrativa completa.';

  leagues: League[] = [];
  isLoading = false;
  searchTerm = '';
  statusFilter = '';
  typeFilter = '';
  totalItems = 0;
  page = 1;
  rowsPerPage = 20;
  feedbackError = '';
  feedbackSuccess = '';

  constructor(private leagueService: LeagueService, private router: Router) {}

  ngOnInit(): void {
    this.fetchLeagues();
  }

  fetchLeagues(): void {
    this.isLoading = true;
    this.leagueService
      .list({
        search: this.searchTerm.trim(),
        status: this.statusFilter,
        league_type: this.typeFilter,
        page: this.page,
      })
      .subscribe({
        next: (response: LeagueListResponse) => {
          this.leagues = response.results || [];
          this.totalItems = response.count || 0;
          this.isLoading = false;
        },
        error: (error) => {
          this.leagues = [];
          this.totalItems = 0;
          this.isLoading = false;
          this.feedbackError = this.extractErrorMessage(error, 'Não foi possível carregar as ligas.');
        },
      });
  }

  onSearch(): void {
    this.page = 1;
    this.fetchLeagues();
  }

  clearFilters(): void {
    this.searchTerm = '';
    this.statusFilter = '';
    this.typeFilter = '';
    this.page = 1;
    this.fetchLeagues();
  }

  createLeague(): void {
    this.router.navigate(['/dashboard/ligas/nova']);
  }

  editLeague(id: number): void {
    this.router.navigate(['/dashboard/ligas', id, 'editar']);
  }

  detailLeague(id: number): void {
    this.router.navigate(['/dashboard/ligas', id]);
  }

  publishLeague(league: League): void {
    this.feedbackError = '';
    this.feedbackSuccess = '';
    this.leagueService.publish(league.id).subscribe({
      next: () => {
        this.feedbackSuccess = `Liga "${league.name}" publicada com sucesso.`;
        this.fetchLeagues();
      },
      error: (error) => {
        this.feedbackError = this.extractErrorMessage(error, 'Não foi possível publicar a liga.');
      },
    });
  }

  cancelLeague(league: League): void {
    this.feedbackError = '';
    this.feedbackSuccess = '';
    this.leagueService.cancel(league.id).subscribe({
      next: () => {
        this.feedbackSuccess = `Liga "${league.name}" cancelada com sucesso.`;
        this.fetchLeagues();
      },
      error: (error) => {
        this.feedbackError = this.extractErrorMessage(error, 'Não foi possível cancelar a liga.');
      },
    });
  }

  onLazyLoad(event: { first?: number }): void {
    const first = event.first ?? 0;
    const newPage = Math.floor(first / this.rowsPerPage) + 1;
    if (newPage !== this.page) {
      this.page = newPage;
      this.fetchLeagues();
    }
  }

  trackByLeagueId(_: number, league: League): number {
    return league.id;
  }

  private extractErrorMessage(error: any, fallback: string): string {
    const response = error?.error;
    if (!response) {
      return fallback;
    }

    if (typeof response === 'string') {
      return response;
    }

    if (typeof response?.detail === 'string') {
      return response.detail;
    }

    const fieldMessages: string[] = [];
    Object.entries(response).forEach(([field, value]) => {
      if (Array.isArray(value)) {
        fieldMessages.push(`${field}: ${value.join(', ')}`);
      } else if (typeof value === 'string') {
        fieldMessages.push(`${field}: ${value}`);
      }
    });

    if (fieldMessages.length > 0) {
      return fieldMessages.join(' | ');
    }

    return fallback;
  }
}
